/** Talking to the server. Every call sends the session cookie; a 401 means log in again. */

import type { Condition, Place, Settings } from "@ohc-price/shared";

export type Language = "en" | "ja" | "zh" | "ko";

export interface User {
  id: number;
  username: string;
  fullName: string;
  isAdmin: boolean;
}

export interface CardHit {
  id: number;
  language: Language;
  setName: string;
  setCode: string | null;
  setTotal: string | null;
  cardName: string;
  localName: string | null;
  variants: string[];
  number: string | null;
  imageUrl: string | null;
  ungraded: number | null;
  psa10: number | null;
  priceDay: string | null;
}

export type Grade = "ungraded" | "grade7" | "grade8" | "grade9" | "grade95" | "psa10" | "bgs10" | "cgc10";

export interface CardDetail extends Omit<CardHit, "ungraded" | "psa10" | "priceDay"> {
  releaseDate: string | null;
  salesVolume: number | null;
  rarity: string | null;
  prices: { day: string | null } & Record<Grade, number | null>;
  history: Array<{ day: string; ungraded: number | null; psa10: number | null }>;
  siblings: Array<{ id: number; variants: string[]; ungraded: number | null; psa10: number | null }>;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers },
  });
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new ApiError(body.error ?? `The server answered ${response.status}`, response.status);
  return body as T;
}

export const api = {
  me: () => call<{ user: User }>("/api/auth/me"),
  login: (username: string, password: string) =>
    call<{ user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    call<{ ok: true }>("/api/auth/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),
  search: (q: string, language?: Language) =>
    call<{ cards: CardHit[] }>(`/api/cards?q=${encodeURIComponent(q)}${language ? `&language=${language}` : ""}`),
  card: (id: number) => call<{ card: CardDetail }>(`/api/cards/${id}`),
};

export type ValuedGrade = "ungraded" | "psa10";

export interface Sale {
  saleId: string;
  soldOn: string;
  priceCents: number;
  title: string | null;
  marketplace: string;
  askedCents: number | null;
}

export interface Sold {
  grade: ValuedGrade;
  estimate: number | null;
  low: number | null;
  high: number | null;
  used: Sale[];
  excluded: Array<{ sale: Sale; reason: string }>;
  flags: Array<{ kind: string; message: string }>;
  readAt: string | null;
  readFailed: string | null;
  currency: "USD";
}

export interface Rate {
  day: string;
  rate: string;
  source: string;
}

export const salesApi = {
  sales: (id: number, grade: ValuedGrade) =>
    call<{ sold: Sold; rate: Rate | null }>(`/api/cards/${id}/sales?grade=${grade}`),
};

// ---------------------------------------------------------------------------
// Evaluations, settings and updates
// ---------------------------------------------------------------------------


export type Verdict = "buy" | "wait" | "pass";
export type Uncertainty = "low" | "medium" | "high";

export interface SettingsVersion {
  id: number;
  values: Settings;
  createdAt: string;
  createdBy: string | null;
  reset: boolean;
}

export interface Figures {
  rate: string | null;
  valueSgd: number | null;
  lowSgd: number | null;
  highSgd: number | null;
  conditionDiscount: number;
  purchaseDiscount: number;
  maxOfferSgd: number | null;
  pricePercentOfValue: number | null;
  differenceSgd: number | null;
}

export interface AiView {
  verdict: Verdict;
  confidence: "low" | "medium" | "high";
  summary: string;
  reasons: string[];
  risks: string[];
  outlook: string;
  sources: Array<{ title: string; url: string }>;
  model: string;
}

export interface Evaluation {
  id: number;
  createdAt: string;
  by?: string;
  grade: ValuedGrade;
  condition: Condition | null;
  place: Place;
  priceKind: "asking" | "offer";
  priceSgdCents: number;
  verdict: Verdict;
  verdictSource: "ai" | "rules";
  uncertainty: Uncertainty;
  note?: string | null;
  card: {
    id: number; language: Language; setName: string; setCode: string | null; setTotal: string | null;
    cardName: string; localName: string | null; variants: string[]; number: string | null;
  };
  figures: Figures;
  rate: Rate | null;
  rules: { verdict: Verdict; reasons: string[]; uncertainty: Uncertainty; uncertaintyReasons: string[] };
  ai: AiView | null;
  aiError: string | null;
  activity: { last30: number; last90: number; daysBetweenSales: number | null; liquidity: string; capped: boolean; exitEstimate: string };
  trend: { recentCents: number | null; earlierCents: number | null; changePercent: number | null; sentence: string };
  sales: { used: Sale[]; excluded: Array<{ sale: Sale; reason: string }>; flags: Array<{ kind: string; message: string }>; readAt: string | null; readFailed: string | null };
  settings: Settings;
  settingVersionId: number;
  valueNowSgdCents?: number | null;
  changePercent?: number | null;
}

export interface EvaluationRow {
  id: number;
  createdAt: string;
  by: string;
  cardId: number;
  card: Evaluation["card"];
  grade: ValuedGrade;
  condition: Condition | null;
  place: Place;
  priceKind: "asking" | "offer";
  priceSgdCents: number;
  valueThenSgdCents: number | null;
  maxOfferSgdCents: number | null;
  valueNowSgdCents: number | null;
  changePercent: number | null;
  verdict: Verdict;
  verdictSource: "ai" | "rules";
  uncertainty: Uncertainty;
  note: string | null;
}

export interface SourceStatus {
  source: string;
  label: string;
  lastOk: string | null;
  lastAttempt: string | null;
  lastError: string | null;
  staleAfterHours: number | null;
  stale: boolean;
  off: string | null;
}

export const evaluateApi = {
  evaluate: (input: { cardId: number; grade: ValuedGrade; condition: Condition | null; place: Place; priceKind: "asking" | "offer"; price: string; note?: string }) =>
    call<{ evaluation: Evaluation }>("/api/evaluations", { method: "POST", body: JSON.stringify(input) }),
  list: (cardId?: number) => call<{ evaluations: EvaluationRow[] }>(`/api/evaluations${cardId ? `?card=${cardId}` : ""}`),
  get: (id: number) => call<{ evaluation: Evaluation }>(`/api/evaluations/${id}`),
  settings: () => call<{ settings: SettingsVersion; aiAvailable: boolean; salesAvailable: boolean }>("/api/settings"),
  saveSettings: (values: Settings) => call<{ settings: SettingsVersion }>("/api/settings", { method: "PUT", body: JSON.stringify(values) }),
  resetSettings: () => call<{ settings: SettingsVersion }>("/api/settings/reset", { method: "POST" }),
  updates: () => call<{ sources: SourceStatus[]; running: boolean }>("/api/updates"),
  changePassword: (currentPassword: string, newPassword: string) => api.changePassword(currentPassword, newPassword),
  runUpdates: () => call<{ started: boolean }>("/api/updates/run", { method: "POST" }),
};
